import Link from 'next/link'
import {
  TrackClick,
  nameToTrackingAction,
} from '@educator/tracking/educatorTracking'
import { paths } from '@educator/utils/paths'
import { IconArrowRight, SubjectSquareLayers } from '@mc/educator-design-system'
import type { CurriculumSubjectItem } from './types'

const CARD_CLASS_NAME = [
  'relative flex h-24 items-center gap-4 overflow-hidden bg-edu-bg-tertiary p-3',
  'md:h-96 md:flex-col md:items-start md:gap-0 md:bg-edu-subject-bg md:p-4',
].join(' ')

const CTA_CLASS_NAME = [
  'hidden shrink-0 border border-edu-fg-semi-dimmed px-4 py-3 no-underline',
  'text-body-3 leading-none font-semibold tracking-[0.65px] text-edu-fg-primary uppercase',
  'transition-colors duration-150 ease-out hover:bg-edu-neutral-1-a12',
  'md:mt-auto md:inline-block',
].join(' ')

interface SubjectLinkProps {
  subject: CurriculumSubjectItem
  label: string
  className: string
  children?: string
}

const SubjectLink = ({
  subject,
  label,
  className,
  children,
}: SubjectLinkProps) => (
  <TrackClick
    event='educatorSignClick'
    action={nameToTrackingAction(subject.name)}
    location='curriculum'
  >
    <Link
      href={`${paths.subjects}/${subject.pageSlug}`}
      aria-label={label}
      className={className}
    >
      {children}
    </Link>
  </TrackClick>
)

interface CurriculumCardProps {
  subject: CurriculumSubjectItem
  ctaLabel: string
  /** Set false to render the card as static content, with no link affordance. */
  withSubjectLinks?: boolean
}

export const CurriculumCard = ({
  subject,
  ctaLabel,
  withSubjectLinks = true,
}: CurriculumCardProps) => (
  <li data-subject={subject.tokenSlug} className={CARD_CLASS_NAME}>
    <div className='relative size-18 shrink-0 overflow-hidden md:size-24'>
      <SubjectSquareLayers />
    </div>
    <div className='flex min-w-0 flex-1 flex-col md:w-full md:flex-none md:gap-3 md:pt-4'>
      {/* Explicit token so the colour follows the card's own `[data-subject]`
          scope rather than inheriting the surrounding surface's foreground. */}
      <h3 className='m-0 text-xl leading-tight text-edu-fg-primary capitalize md:text-body-1-emphasis'>
        {subject.name}
      </h3>
      <p className='m-0 hidden text-body-3 leading-body-3 text-edu-fg-semi-dimmed md:block'>
        {subject.description}
      </p>
    </div>
    {withSubjectLinks && subject.pageSlug && (
      <>
        <IconArrowRight className='shrink-0 md:hidden' />
        <SubjectLink
          subject={subject}
          label={subject.name}
          className='absolute inset-0 md:hidden'
        />
        <SubjectLink
          subject={subject}
          label={`${ctaLabel}: ${subject.name}`}
          className={CTA_CLASS_NAME}
        >
          {ctaLabel}
        </SubjectLink>
      </>
    )}
  </li>
)
